// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import AppLayout from '../components/AppLayout';
import LoadingSpinner from '../components/LoadingSpinner';

function SettingsPage() {
  const { user } = useAuthStore();
  
  // Verifica se o usuário é administrador do tenant
  const isAdmin = user?.roles?.some(role => 
    ['tenant_admin', 'admin', 'tannat'].includes(role.slug) || 
    role.permissions?.includes('level:full_access')
  );

  const [activeTab, setActiveTab] = useState('profile');
  
  // Estado de carregamento da página inteira
  const [loadingTenant, setLoadingTenant] = useState(false);
  const [tenantError, setTenantError] = useState('');

  // Estados do formulário de Perfil
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [profileSuccess, setProfileSuccess] = useState('');
  const [updatingProfile, setUpdatingProfile] = useState(false);

  // Estados do formulário de Palavra-passe
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  // Estados do formulário da Instituição
  const [tenantData, setTenantData] = useState({
    name: '',
    institution_type: 'igreja',
    tax_id: '',
    phone: '',
    expected_members: '',
    calendar_iframe: '',
  });
  const [tenantErrors, setTenantErrors] = useState({});
  const [tenantSuccess, setTenantSuccess] = useState('');
  const [updatingTenant, setUpdatingTenant] = useState(false);

  // Efeito para carregar dados da Instituição se for administrador
  useEffect(() => {
    if (isAdmin) {
      setLoadingTenant(true);
      setTenantError('');
      api.get('/tenants/me')
        .then(response => {
          const data = response.data.data;
          setTenantData({
            name: data.name || '',
            institution_type: data.settings?.institution_type || 'igreja',
            tax_id: data.tax_id || '',
            phone: data.phone || '',
            expected_members: data.settings?.expected_members || '',
            calendar_iframe: data.settings?.calendar_iframe || '',
          });
        })
        .catch(err => {
          console.error('Erro ao carregar dados do tenant:', err);
          setTenantError('Não foi possível carregar os dados da instituição.');
        })
        .finally(() => {
          setLoadingTenant(false);
        });
    }
  }, [isAdmin]);

  // Bloquear copiar, colar e cortar nos campos de password
  const preventPasteCopyCut = (e) => {
    e.preventDefault();
  };

  // Handler de mudança de campo do perfil
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    if (profileErrors[name]) {
      setProfileErrors(prev => ({ ...prev, [name]: '' }));
    }
    setProfileSuccess('');
  };

  // Handler de mudança de campo de password
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
    setPasswordSuccess('');
  };

  // Handler de mudança de campo da instituição
  const handleTenantChange = (e) => {
    const { name, value } = e.target;
    setTenantData(prev => ({ ...prev, [name]: value }));
    if (tenantErrors[name]) {
      setTenantErrors(prev => ({ ...prev, [name]: '' }));
    }
    setTenantSuccess('');
  };

  // Envio do formulário de Perfil
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileErrors({});
    setProfileSuccess('');
    
    // Validação local
    const errors = {};
    if (!profileData.name.trim()) errors.name = 'O nome é obrigatório';
    if (!profileData.email.trim()) errors.email = 'O email é obrigatório';
    
    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setUpdatingProfile(true);
    try {
      const response = await api.put('/auth/profile', {
        name: profileData.name.trim(),
        email: profileData.email.trim(),
      });

      if (response.data.success) {
        setProfileSuccess('Perfil atualizado com sucesso!');
        // Atualiza dados no store de autenticação global
        useAuthStore.setState({
          user: {
            ...useAuthStore.getState().user,
            name: response.data.data.name,
            email: response.data.data.email,
          }
        });
      }
    } catch (err) {
      console.error('Erro ao atualizar perfil:', err);
      const backendErrors = err.response?.data?.error?.details || {};
      if (err.response?.data?.message) {
        setProfileErrors({ general: err.response.data.message });
      } else {
        setProfileErrors(backendErrors);
      }
    } finally {
      setUpdatingProfile(false);
    }
  };

  // Envio do formulário de Password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordErrors({});
    setPasswordSuccess('');

    // Validação local
    const errors = {};
    if (!passwordData.current_password) errors.current_password = 'A senha atual é obrigatória';
    if (!passwordData.new_password) errors.new_password = 'A nova senha é obrigatória';
    if (passwordData.new_password.length < 8) errors.new_password = 'A nova senha deve ter no mínimo 8 caracteres';
    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      errors.new_password_confirmation = 'A confirmação não coincide com a nova senha';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setUpdatingPassword(true);
    try {
      const response = await api.put('/auth/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        new_password_confirmation: passwordData.new_password_confirmation,
      });

      if (response.data.success) {
        setPasswordSuccess('Senha alterada com sucesso!');
        setPasswordData({
          current_password: '',
          new_password: '',
          new_password_confirmation: '',
        });
      }
    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      if (err.response?.data?.message) {
        setPasswordErrors({ general: err.response.data.message });
      } else {
        setPasswordErrors({ general: 'Erro ao alterar a senha. Verifique os dados e tente novamente.' });
      }
    } finally {
      setUpdatingPassword(false);
    }
  };

  // Envio do formulário de Instituição
  const handleTenantSubmit = async (e) => {
    e.preventDefault();
    setTenantErrors({});
    setTenantSuccess('');

    // Validação local
    const errors = {};
    if (!tenantData.name.trim()) errors.name = 'O nome da instituição é obrigatório';

    if (Object.keys(errors).length > 0) {
      setTenantErrors(errors);
      return;
    }

    setUpdatingTenant(true);
    try {
      const response = await api.put('/tenants/me', {
        name: tenantData.name.trim(),
        tax_id: tenantData.tax_id.trim() || null,
        phone: tenantData.phone.trim() || null,
        settings: {
          institution_type: tenantData.institution_type,
          expected_members: parseInt(tenantData.expected_members) || 0,
          calendar_iframe: tenantData.calendar_iframe,
        }
      });

      if (response.data.success) {
        setTenantSuccess('Dados da instituição atualizados com sucesso!');
      }
    } catch (err) {
      console.error('Erro ao atualizar instituição:', err);
      if (err.response?.data?.message) {
        setTenantErrors({ general: err.response.data.message });
      } else {
        setTenantErrors({ general: 'Erro ao atualizar dados da instituição.' });
      }
    } finally {
      setUpdatingTenant(false);
    }
  };

  return (
    <AppLayout>
      <div className="settings-page" style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
        
        {/* Cabeçalho */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
            Configurações
          </h1>
          <p style={{ color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            Gerencie as informações da sua conta, palavra-passe e dados da instituição.
          </p>
        </div>

        {/* Tabs de navegação */}
        {isAdmin && (
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '2rem', gap: '1.5rem' }}>
            <button
              onClick={() => setActiveTab('profile')}
              style={{
                paddingBottom: '0.75rem',
                fontSize: '1rem',
                fontWeight: '600',
                color: activeTab === 'profile' ? 'var(--primary)' : 'var(--text-muted)',
                borderBottom: activeTab === 'profile' ? '2px solid var(--primary)' : '2px solid transparent',
                background: 'none',
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Minha Conta
            </button>
            <button
              onClick={() => setActiveTab('institution')}
              style={{
                paddingBottom: '0.75rem',
                fontSize: '1rem',
                fontWeight: '600',
                color: activeTab === 'institution' ? 'var(--primary)' : 'var(--text-muted)',
                borderBottom: activeTab === 'institution' ? '2px solid var(--primary)' : '2px solid transparent',
                background: 'none',
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Dados da Instituição
            </button>
          </div>
        )}

        {/* TAB 1: Minha Conta (Perfil + Password) */}
        {(activeTab === 'profile' || !isAdmin) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem' }}>
            
            {/* Bloco de Dados Cadastrais do Utilizador */}
            <div className="card" style={{ padding: '2rem', borderRadius: '12px', backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <span className="material-symbols-outlined" style={{ color: 'var(--primary)', fontSize: '1.5rem' }}>person</span>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Dados do Utilizador</h2>
              </div>

              {profileSuccess && (
                <div className="alert alert-success" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined">check_circle</span>
                  <span>{profileSuccess}</span>
                </div>
              )}
              {profileErrors.general && (
                <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
                  {profileErrors.general}
                </div>
              )}

              <form onSubmit={handleProfileSubmit}>
                <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                  <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Nome Completo</label>
                  <input
                    type="text"
                    name="name"
                    className={`form-input ${profileErrors.name ? 'error' : ''}`}
                    value={profileData.name}
                    onChange={handleProfileChange}
                    placeholder="Seu nome completo"
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                  {profileErrors.name && (
                    <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      {profileErrors.name}
                    </span>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Endereço de Email</label>
                  <input
                    type="email"
                    name="email"
                    className={`form-input ${profileErrors.email ? 'error' : ''}`}
                    value={profileData.email}
                    onChange={handleProfileChange}
                    placeholder="seu.email@provedor.com"
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                  {profileErrors.email && (
                    <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      {profileErrors.email}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={updatingProfile}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    {updatingProfile ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </form>
            </div>

            {/* Bloco de Alteração de Palavra-passe */}
            <div className="card" style={{ padding: '2rem', borderRadius: '12px', backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <span className="material-symbols-outlined" style={{ color: 'var(--primary)', fontSize: '1.5rem' }}>lock</span>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Alterar Senha</h2>
              </div>

              <div style={{
                backgroundColor: 'var(--background)',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: 'var(--text-muted)',
                marginBottom: '1.5rem',
                borderLeft: '4px solid var(--primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '1.25rem' }}>security</span>
                <span>Por motivos de segurança, a cópia e colagem foram desativadas nos campos de senha.</span>
              </div>

              {passwordSuccess && (
                <div className="alert alert-success" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined">check_circle</span>
                  <span>{passwordSuccess}</span>
                </div>
              )}
              {passwordErrors.general && (
                <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
                  {passwordErrors.general}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit}>
                {/* Senha Atual */}
                <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                  <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Senha Atual</label>
                  <input
                    type="password"
                    name="current_password"
                    autoComplete="new-password"
                    onPaste={preventPasteCopyCut}
                    onCopy={preventPasteCopyCut}
                    onCut={preventPasteCopyCut}
                    className={`form-input ${passwordErrors.current_password ? 'error' : ''}`}
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    placeholder="Digite sua senha atual"
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                  {passwordErrors.current_password && (
                    <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      {passwordErrors.current_password}
                    </span>
                  )}
                </div>

                {/* Nova Senha */}
                <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                  <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Nova Senha</label>
                  <input
                    type="password"
                    name="new_password"
                    autoComplete="new-password"
                    onPaste={preventPasteCopyCut}
                    onCopy={preventPasteCopyCut}
                    onCut={preventPasteCopyCut}
                    className={`form-input ${passwordErrors.new_password ? 'error' : ''}`}
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    placeholder="Mínimo de 8 caracteres"
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                  {passwordErrors.new_password && (
                    <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      {passwordErrors.new_password}
                    </span>
                  )}
                </div>

                {/* Confirmar Nova Senha */}
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Confirmar Nova Senha</label>
                  <input
                    type="password"
                    name="new_password_confirmation"
                    autoComplete="new-password"
                    onPaste={preventPasteCopyCut}
                    onCopy={preventPasteCopyCut}
                    onCut={preventPasteCopyCut}
                    className={`form-input ${passwordErrors.new_password_confirmation ? 'error' : ''}`}
                    value={passwordData.new_password_confirmation}
                    onChange={handlePasswordChange}
                    placeholder="Repita a nova senha"
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                  />
                  {passwordErrors.new_password_confirmation && (
                    <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                      {passwordErrors.new_password_confirmation}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={updatingPassword}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    {updatingPassword ? 'Alterando...' : 'Alterar Senha'}
                  </button>
                </div>
              </form>
            </div>

          </div>
        )}

        {/* TAB 2: Dados da Instituição (Onboarding settings) */}
        {activeTab === 'institution' && isAdmin && (
          <div className="card" style={{ padding: '2rem', borderRadius: '12px', backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <span className="material-symbols-outlined" style={{ color: 'var(--primary)', fontSize: '1.5rem' }}>church</span>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Dados da Instituição</h2>
            </div>

            {loadingTenant ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
                <LoadingSpinner size="md" />
              </div>
            ) : tenantError ? (
              <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
                {tenantError}
              </div>
            ) : (
              <>
                {tenantSuccess && (
                  <div className="alert alert-success" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="material-symbols-outlined">check_circle</span>
                    <span>{tenantSuccess}</span>
                  </div>
                )}
                {tenantErrors.general && (
                  <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
                    {tenantErrors.general}
                  </div>
                )}

                <form onSubmit={handleTenantSubmit}>
                  
                  {/* Nome da Organização */}
                  <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                    <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Nome da Igreja ou Ministério</label>
                    <input
                      type="text"
                      name="name"
                      className={`form-input ${tenantErrors.name ? 'error' : ''}`}
                      value={tenantData.name}
                      onChange={handleTenantChange}
                      placeholder="Ex: Igreja Vida Nova"
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                      required
                    />
                    {tenantErrors.name && (
                      <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                        {tenantErrors.name}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                    
                    {/* Tipo de Instituição */}
                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Tipo de Instituição</label>
                      <select
                        name="institution_type"
                        className="form-input"
                        value={tenantData.institution_type}
                        onChange={handleTenantChange}
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                      >
                        <option value="igreja">Igreja</option>
                        <option value="ministerio">Ministério</option>
                        <option value="missao">Missão</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>

                    {/* Identificação Fiscal */}
                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>NIF / CNPJ / VAT</label>
                      <input
                        type="text"
                        name="tax_id"
                        className="form-input"
                        value={tenantData.tax_id}
                        onChange={handleTenantChange}
                        placeholder="Ex: 501234567"
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
                    
                    {/* Telefone / WhatsApp */}
                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Telefone de Contato</label>
                      <input
                        type="tel"
                        name="phone"
                        className="form-input"
                        value={tenantData.phone}
                        onChange={handleTenantChange}
                        placeholder="Ex: +351 912 345 678"
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                      />
                    </div>

                    {/* Membros Esperados */}
                    <div className="form-group">
                      <label className="form-label" style={{ fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Número Esperado de Membros</label>
                      <input
                        type="number"
                        name="expected_members"
                        className="form-input"
                        value={tenantData.expected_members}
                        onChange={handleTenantChange}
                        placeholder="Ex: 150"
                        min="0"
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}
                      />
                    </div>
                    

                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={updatingTenant}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      {updatingTenant ? 'Salvando...' : 'Salvar Instituição'}
                    </button>
                  </div>

                </form>
              </>
            )}

          </div>
        )}

      </div>
    </AppLayout>
  );
}

export default SettingsPage;
